import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader, Toast, Modal } from "@/components/Layout";
import api from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || "Eroare";
const ACT = { cs: "Curs", lp: "Lucrări practice", sem: "Seminar" };
const SEM = { 1: "Semestrul I", 2: "Semestrul II" };
const fmt = (n) => (Math.round(n * 100) / 100).toString();
let KEY = 0;

/** Alocarea pe semestre: pentru fiecare activitate a unei discipline, bifezi seriile/grupele concrete care o primesc. */
export default function Allocation() {
  const { planId } = useParams();
  const [d, setD] = useState(null);
  const [rows, setRows] = useState([]);          // plat: câte un rând pe (disciplină, semestru, activitate)
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState({});
  const [confirmDefault, setConfirmDefault] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3500); };

  const apply = (data) => {
    setD(data);
    setRows(data.disciplines.flatMap(x => x.rows.map(r => ({ ...r, key: ++KEY, disc: x }))));
    setDirty(false);
  };
  const load = () => api.get(`/stat-didactic/plans/${planId}/allocation`).then(r => apply(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, [planId]);
  if (d === null) return <div>Se încarcă...</div>;
  if (d === false) return <div>Plan inexistent. <Link to="/stat-didactic/planuri">Înapoi</Link></div>;

  const forms = Object.fromEntries(d.formations.map(f => [f.id, f]));
  const byKind = (kinds) => d.formations.filter(f => kinds.includes(f.kind));
  const upd = (key, patch) => { setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r))); setDirty(true); };
  const toggle = (r, fid) => upd(r.key, { formation_ids: r.formation_ids.includes(fid) ? r.formation_ids.filter(x => x !== fid) : [...r.formation_ids, fid] });

  const save = async () => {
    try {
      const body = { rows: rows.map(r => ({ discipline_id: r.discipline_id, semester: Number(r.semester), activity: r.activity,
        hours_week: Number(r.hours_week) || 0, hours_total: r.hours_total === "" || r.hours_total === null ? null : Number(r.hours_total), formation_ids: r.formation_ids })) };
      const res = await api.put(`/stat-didactic/plans/${planId}/allocation`, body);
      apply(res.data); toast("Alocare salvată", "success");
    } catch (e) { toast(errText(e), "error"); }
  };
  const makeDefault = async () => {
    setConfirmDefault(false);
    try { const res = await api.post(`/stat-didactic/plans/${planId}/allocation/default`); apply(res.data); toast("Alocare implicită generată", "success"); }
    catch (e) { toast(errText(e), "error"); }
  };

  const grouped = d.disciplines.map(x => ({ ...x, rows: rows.filter(r => r.discipline_id === x.id) }));
  const anySaved = d.disciplines.some(x => x.saved);
  return (
    <>
      <PageHeader title={`Alocare pe semestre — ${d.program.name}, anul ${d.plan.study_year}`} subtitle={`Plan ${d.year.name} · ${d.year.weeks_per_semester} săptămâni pe semestru`}
        actions={<>
          <Link className="btn btn-secondary" to={`/stat-didactic/planuri/${planId}`}>← Plan</Link>
          <button className="btn btn-secondary" onClick={() => (anySaved ? setConfirmDefault(true) : makeDefault())} data-testid="sfd-alloc-default">Alocare implicită</button>
          <button className="btn btn-primary" disabled={!dirty && anySaved} onClick={save} data-testid="sfd-alloc-save">Salvează alocarea</button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />
      {!d.cohort && <div className="te-wait" data-testid="sfd-alloc-nocohort">Nu există efectiv și formațiuni pentru acest an de studiu. Creează-le din <Link to={`/stat-didactic/formatiuni?program=${d.plan.program_id}&year=${d.plan.academic_year_id}`}>Formațiuni de studii</Link>, apoi revino aici.</div>}
      {!anySaved && d.cohort && <div className="te-wait">Alocarea de mai jos este cea implicită (ca în plan, către toate formațiunile potrivite) și nu este salvată încă.</div>}
      {dirty && <div className="te-closed">Ai modificări nesalvate.</div>}

      {grouped.map(x => (
        <div className="card" style={{ padding: 14, marginBottom: 10 }} key={x.id} data-testid={`sfd-alloc-disc-${x.name}`}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{x.name} <span style={{ fontWeight: 400, color: "#64748B", fontSize: 12 }}>· {x.disc_type} · {x.category}{x.option_group ? ` (${x.option_group})` : ""}</span></div>
          <table className="tbl" style={{ fontSize: 12.5 }}>
            <thead><tr><th>Semestru</th><th>Activitate</th><th title="Ore pe săptămână în plan">Plan</th><th>Ore/săpt.</th><th title="Total manual pe semestru, dacă diferă de ore × săptămâni">Total manual</th><th>Formațiuni</th><th>Ore fizice/sem.</th></tr></thead>
            <tbody>{x.rows.map(r => {
              const elig = byKind(r.eligible_kinds);
              return (
                <React.Fragment key={r.key}>
                  <tr data-testid={`sfd-alloc-row-${x.name}-${r.semester}-${r.activity}`}>
                    <td><select className="select" style={{ padding: "3px 4px" }} value={r.semester} onChange={e => upd(r.key, { semester: Number(e.target.value) })}>{[1, 2].map(n => <option key={n} value={n}>{SEM[n]}</option>)}</select></td>
                    <td>{ACT[r.activity]}</td>
                    <td>{fmt(r.plan_hours_week)}</td>
                    <td><input className="input" type="number" min="0" step="0.5" style={{ width: 70, padding: "3px 6px" }} value={r.hours_week} onChange={e => upd(r.key, { hours_week: e.target.value })} /></td>
                    <td><input className="input" type="number" min="0" style={{ width: 80, padding: "3px 6px" }} value={r.hours_total ?? ""} placeholder="—" onChange={e => upd(r.key, { hours_total: e.target.value })} /></td>
                    <td><button className="btn btn-secondary" style={{ padding: "2px 10px", fontSize: 12 }} onClick={() => setOpen({ ...open, [r.key]: !open[r.key] })}>
                      {r.formation_ids.length}/{elig.length} {r.eligible_kinds[0] === "serie" ? "serii" : "grupe"} {open[r.key] ? "▴" : "▾"}</button></td>
                    <td style={{ fontWeight: 600 }}>{fmt(r.physical_semester)}</td>
                  </tr>
                  {open[r.key] && (
                    <tr><td colSpan={7} style={{ background: "#F8FAFC" }}>
                      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                        <button className="btn btn-ghost" style={{ padding: "1px 8px", fontSize: 12 }} onClick={() => upd(r.key, { formation_ids: elig.map(f => f.id) })}>Toate</button>
                        <button className="btn btn-ghost" style={{ padding: "1px 8px", fontSize: 12 }} onClick={() => upd(r.key, { formation_ids: [] })}>Niciuna</button>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
                        {elig.map(f => (
                          <label key={f.id} style={{ fontSize: 12.5, display: "flex", gap: 5, alignItems: "center" }}>
                            <input type="checkbox" checked={r.formation_ids.includes(f.id)} onChange={() => toggle(r, f.id)} />
                            {f.name} <span style={{ color: "#94A3B8" }}>({f.students})</span>
                          </label>))}
                        {elig.length === 0 && <span style={{ color: "#94A3B8", fontSize: 12.5 }}>Nu există formațiuni de acest tip.</span>}
                      </div>
                    </td></tr>)}
                </React.Fragment>);
            })}</tbody>
          </table>
        </div>))}

      <div className="card" style={{ padding: 18, marginTop: 14 }} data-testid="sfd-alloc-summary">
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Ore fizice de normat <span style={{ fontWeight: 400, color: "#64748B", fontSize: 12 }}>(ore × formațiuni bifate × săptămâni; se transmit Notei de comandă)</span></div>
        <table className="tbl" style={{ maxWidth: 640 }}>
          <thead><tr><th></th>{Object.values(ACT).map(l => <th key={l}>{l}</th>)}</tr></thead>
          <tbody>{[1, 2].map(n => (<tr key={n}><td style={{ fontWeight: 600 }}>{SEM[n]}</td>{Object.keys(ACT).map(a => <td key={a}>{fmt(d.summary[n][a].physical_hours)}</td>)}</tr>))}</tbody>
        </table>
        <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 6 }}>Totalurile se actualizează la salvare.</div>
      </div>
      {d.warnings.length > 0 && (
        <div className="te-closed" style={{ marginTop: 12 }} data-testid="sfd-alloc-warnings">
          <b>Atenție</b><ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{d.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>)}
      {confirmDefault && (
        <Modal title="Rescrii alocarea cu cea implicită?" onClose={() => setConfirmDefault(false)} width={480}>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 16 }}>Alocarea salvată (inclusiv semestrele mutate și bifările tale) se înlocuiește cu cea din plan, către toate formațiunile potrivite.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={() => setConfirmDefault(false)}>Anulează</button>
            <button className="btn btn-primary" onClick={makeDefault} data-testid="sfd-alloc-default-confirm">Rescrie</button>
          </div>
        </Modal>)}
    </>
  );
}
